import React, { useEffect, useRef, useState } from 'react';

import { SerializedStyles } from '@emotion/react';
import IconButton from '@mui/material/IconButton';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import { useLocation } from 'react-router-dom';

import CloseIcon from '../../assets/icons/CloseIcon';
import SearchIcon from '../../assets/icons/SearchIcon';
import styled from '../../styled';
import { colorsPalette, SMALL_MARGIN_PX } from '../../theme';
import useSearchClient from '../../utils/hooks/useSearchClient';
import QuerySuggestions from './QuerySuggestions';
import useStyles from './styles';

interface SearchProps {
  currentRefinement?: string;
  wrapperClass?: string;
  refine: Function;
  handleClear?: Function;
  withClear?: boolean;
  withAutocomplete?: boolean;
  searchPlaceholder?: string;
  wrapperStyles?: SerializedStyles | ((props: any) => SerializedStyles);
}

const Search: React.FC<SearchProps> = ({
  currentRefinement = '',
  refine,
  handleClear = undefined,
  withClear = false,
  withAutocomplete = false,
  searchPlaceholder,
  wrapperStyles,
}) => {
  const location = useLocation();
  const classes = useStyles();
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { searchClient, indexName } = useSearchClient('query_suggestions');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('s');
    if (s) {
      refine(s);
    }
  }, [location, refine]);

  const onChange = (e) => {
    const value = e?.target?.value;
    setValue(value || '');
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      inputRef?.current?.blur();
    }
  };

  const resetSearch = () => {
    setValue('');
    handleClear && handleClear();
  };

  const search = (newValue?: string) => {
    refine(newValue || value);
  };

  useEffect(() => {
    setValue(currentRefinement);
  }, [currentRefinement, location]);

  return (
    <InstantSearch searchClient={searchClient} indexName={indexName}>
      <Configure hitsPerPage={10} distinct={3} clickAnalytics />
      <QuerySuggestionsWrapper optionalStyles={wrapperStyles}>
        {withClear && value && (
          <IconButton
            data-testid="clearSearchBox"
            className={classes.closeIconWrapper}
            aria-label="clear"
            onClick={resetSearch}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        )}
        <QuerySuggestions
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          inputRef={inputRef}
          withAutocomplete={withAutocomplete}
          search={search}
          placeholder={searchPlaceholder}
        />
        <IconButton
          data-testid="submitSearch"
          type="submit"
          aria-label="search"
          onClick={() => search()}
          className={classes.searchIcon}
          size="large"
        >
          <SearchIcon />
        </IconButton>
      </QuerySuggestionsWrapper>
    </InstantSearch>
  );
};

export default Search;

const QuerySuggestionsWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  height: 60px;
  width: 100%;
  max-width: 100%;
  margin-bottom: ${SMALL_MARGIN_PX};
  padding-left: 30px;
  padding-right: 9px;
  border-radius: 999em;
  background: ${colorsPalette.whiteSecondary};

  & .MuiAutocomplete-root {
    width: 100%;
    display: flex;
    align-items: center;
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: 600px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${SMALL_MARGIN_PX};
  }

  ${({ optionalStyles }) => optionalStyles}
`;
